import React, { useEffect } from "react";
import useGet from "../../../hooks/read/useGet";
import Pagination from "../../pagination";
import InlineLoader from "../../loader/InlineLoader";
import { CalendarIcon } from "@heroicons/react/solid";

const getLeaveBadgeStyle = (name) => {
  const lower = (name || "").toLowerCase();
  if (lower.includes("privilege") || lower.includes("pl")) {
    return "bg-purple-50 text-purple-700 border-purple-200";
  }
  if (lower.includes("casual") || lower.includes("causal") || lower.includes("cl")) {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (lower.includes("sick") || lower.includes("sl")) {
    return "bg-amber-50 text-amber-700 border-amber-200";
  }
  if (lower.includes("optional") || lower.includes("ol")) {
    return "bg-cyan-50 text-cyan-700 border-cyan-200";
  }
  return "bg-blue-50 text-blue-700 border-blue-200";
};

const LeavesUtilised = ({ currentYear, companyEmployeeId }) => {
  const yearVal =
    currentYear instanceof Date
      ? currentYear.getFullYear()
      : currentYear || new Date().getFullYear();

  const { list, paramsObject, setGetListParams } = useGet({
    url: "getUtilizedLeaveListing",
    initialData: {
      month_year: yearVal,
      page_no: 0,
      limit: 10,
      ...(companyEmployeeId ? { company_employee_id: companyEmployeeId } : {}),
    },
  });

  useEffect(() => {
    const nextYear =
      currentYear instanceof Date
        ? currentYear.getFullYear()
        : currentYear || new Date().getFullYear();

    setGetListParams((prev) => {
      const nextParams = {
        ...prev,
        month_year: nextYear,
        page_no: 0,
      };
      if (companyEmployeeId) {
        nextParams.company_employee_id = companyEmployeeId;
      } else {
        delete nextParams.company_employee_id;
      }
      return nextParams;
    });
  }, [currentYear, companyEmployeeId]);

  const data = list?.data;
  const leavesList = data?.data || [];
  const isLoading = list?.isLoading;

  const handlePageClick = (val) => {
    const updatedParams = {
      ...paramsObject,
      page_no: val.selected,
    };
    setGetListParams(updatedParams);
  };

  const formatDate = (date, month, year) => {
    if (!date || !month || !year) return "-";
    return `${String(date).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  };

  const totalDeductedCount = leavesList.reduce(
    (acc, curr) => acc + (Number(curr.redeem_leave_count) || 1),
    0
  );

  return (
    <div className="w-full space-y-4">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <InlineLoader className="text-primary w-8 h-8" />
          <span className="text-gray-500 text-sm mt-3 font-medium">
            Fetching utilized leaves...
          </span>
        </div>
      ) : leavesList.length <= 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-center bg-gray-50/70 rounded-2xl border border-dashed border-gray-200 p-8">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-3">
            <CalendarIcon className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-gray-800">
            No Utilized Leaves Found
          </h3>
          <p className="text-sm text-gray-500 max-w-sm mt-1">
            No leave redemptions or utilized leaves recorded for the year {yearVal}.
          </p>
        </div>
      ) : (
        <>
          {/* Summary Stats Pill */}
          <div className="flex flex-wrap items-center gap-3 bg-blue-50/70 border border-blue-100 rounded-xl px-4 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-blue-700">Total Deducted Leaves:</span>
              <span className="text-xs font-bold text-blue-900 bg-white px-2.5 py-0.5 rounded-full border border-blue-200 shadow-sm">
                {totalDeductedCount} Day{totalDeductedCount !== 1 ? "s" : ""}
              </span>
            </div>
            <span className="text-blue-300 hidden sm:inline">•</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-blue-700">Total Records:</span>
              <span className="text-xs font-bold text-blue-900 bg-white px-2.5 py-0.5 rounded-full border border-blue-200 shadow-sm">
                {data?.total_count || leavesList.length}
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <th className="py-3 px-4 text-center w-16">Sr.No</th>
                    <th className="py-3 px-4 text-center w-44">Leave Date</th>
                    <th className="py-3 px-4">Leave Type</th>
                    <th className="py-3 px-4 text-center w-36">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {leavesList.map((el, idx) => {
                    const pageIndex =
                      (paramsObject?.page_no || 0) * (paramsObject?.limit || 10) +
                      idx +
                      1;
                    const countNum = Number(el.redeem_leave_count) || 1;
                    return (
                      <tr
                        key={el.id || idx}
                        className="hover:bg-blue-50/40 transition-colors"
                      >
                        <td className="py-3.5 px-4 text-center font-medium text-gray-500">
                          {pageIndex}
                        </td>
                        <td className="py-3.5 px-4 text-center font-medium text-gray-800">
                          <span className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-md text-xs font-semibold text-gray-700">
                            <CalendarIcon className="w-3.5 h-3.5 text-gray-400" />
                            {formatDate(
                              el.attendance_date,
                              el.attendance_month,
                              el.attendance_year
                            )}
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getLeaveBadgeStyle(
                              el.redeem_leave_head_name
                            )}`}
                          >
                            {el.redeem_leave_head_name || "Leave"}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <span className="inline-block font-semibold text-gray-700 text-xs bg-gray-100 border border-gray-200 px-2.5 py-1 rounded-md">
                            {countNum === 0.5
                              ? "0.5 Day (Half)"
                              : `${countNum} Day${countNum > 1 ? "s" : ""}`}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {data?.total_count > (data?.per_page || 10) && (
            <div className="pt-2">
              <Pagination
                lengthofItems={data?.total_count}
                limit={data?.per_page || 10}
                currentPage={data?.page_no || 0}
                onPageChange={handlePageClick}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeavesUtilised;
